import { UNSEATED_GROUP_ID } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/constants';
import {
    SeatingTicket,
    TransformedSeatingGroup,
} from 'assets/js/pages/campaign-settings/campaign-event/table-seating/types';

const compactSeats = (
    tickets: Record<string, SeatingTicket>,
    seatingGroupId: string,
): Record<string, SeatingTicket> => {
    const ticketsInGroup = Object.values(tickets)
        .filter(t => t.seating_group_id === seatingGroupId)
        .sort((a, b) => (a.seat_number || 0) - (b.seat_number || 0));

    return ticketsInGroup.reduce<Record<string, SeatingTicket>>((updates, ticket, index) => {
        const newSeatNumber = indexToSeatNumber(index);
        if (ticket.seat_number !== newSeatNumber) {
            updates[ticket.id] = {
                ...ticket,
                seat_number: newSeatNumber,
            };
        }
        return updates;
    }, {});
};

const getNextTableNumber = (seatingGroups: Record<string, TransformedSeatingGroup>): number => {
    const tableNumbers = Object.values(seatingGroups)
        .filter(g => g.id !== UNSEATED_GROUP_ID)
        .map(g => g.number)
        .filter(n => typeof n === 'number');

    return tableNumbers.length === 0 ? 1 : Math.max(...tableNumbers) + 1;
};

const canRemoveTable = (
    tableId: string,
    seatingGroups: Record<string, TransformedSeatingGroup>,
    tickets: Record<string, SeatingTicket>,
): boolean => {
    if (tableId === UNSEATED_GROUP_ID) return false;

    const table = seatingGroups[tableId];
    if (!table) return false;

    const remainingTables = Object.values(seatingGroups).filter(
        g => g.id !== UNSEATED_GROUP_ID && g.id !== tableId,
    );
    if (remainingTables.length === 0) return false;

    const hasOccupants = Object.values(tickets).some(t => t.seating_group_id === tableId);

    return !hasOccupants;
};

const getTableOccupancy = (tableId: string, tickets: Record<string, SeatingTicket>): number => {
    return Object.values(tickets).reduce(
        (count, ticket) => (ticket.seating_group_id === tableId ? count + 1 : count),
        0,
    );
};

const getContiguousGroupMembersFromSeat = (
    groupId: string,
    tableId: string,
    params: {
        startSeatNumber: number;
        tickets?: Record<string, SeatingTicket>;
        sortedTableTickets?: SeatingTicket[];
    },
): string[] => {
    const { startSeatNumber, tickets, sortedTableTickets } = params;

    if (!sortedTableTickets && !tickets) {
        return [];
    }

    const tableTickets =
        sortedTableTickets ||
        (tickets
            ? Object.values(tickets)
                  .filter(t => t.seating_group_id === tableId)
                  .sort((a, b) => (a.seat_number || 0) - (b.seat_number || 0))
            : []);

    const startIndex = tableTickets.findIndex(
        t => t.seat_number === startSeatNumber && t.ticket_group_id === groupId,
    );

    if (startIndex === -1) return [];

    const remainingTickets = tableTickets.slice(startIndex);
    const groupEndIndex = remainingTickets.findIndex(t => t.ticket_group_id !== groupId);
    const contiguousMembers =
        groupEndIndex === -1 ? remainingTickets : remainingTickets.slice(0, groupEndIndex);

    return contiguousMembers.map(t => t.id);
};

const indexToSeatNumber = (index: number): number => index + 1;

const seatNumberToIndex = (seatNumber: number): number => seatNumber - 1;

const createSeatNumberArray = (capacity: number): number[] =>
    Array.from({ length: capacity }, (_, i) => indexToSeatNumber(i));

export {
    compactSeats,
    getNextTableNumber,
    canRemoveTable,
    getTableOccupancy,
    getContiguousGroupMembersFromSeat,
    indexToSeatNumber,
    seatNumberToIndex,
    createSeatNumberArray,
};
